import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Copy, Download, Camera, Calendar, Share2 } from 'lucide-react';
import {
  RefreshCw,
  Phone,
  Users,
  MessageSquare,
  Target,
  FileText,
  IndianRupee,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Clock,
  ChevronDown,
  ChevronUp,
  Plus,
  X,
  Search,
  Mail,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { DashboardLayout } from '@/components/DashboardLayout';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { api, Lead } from '@/lib/api';
import {
  DailyKPIData,
  DailyKPIMetric,
  RevenueKPIMetric,
  TeamKPIData,
  TeamMemberKPI,
  MemberStatus,
  LeadSourceKPI,
} from '@/types/dailyKPI';

// ==========================================
// HELPERS
// ==========================================

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(value);

const formatDate = (dateStr: string) => {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-IN', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const getProgressColor = (pct: number) => {
  if (pct >= 80) return 'bg-green-500';
  if (pct >= 50) return 'bg-yellow-500';
  return 'bg-red-500';
};

const getProgressBg = (pct: number) => {
  if (pct >= 80) return 'bg-green-100';
  if (pct >= 50) return 'bg-yellow-100';
  return 'bg-red-100';
};

const getStatusBadge = (status: MemberStatus) => {
  switch (status) {
    case 'ON_TRACK':
      return <Badge className="bg-green-100 text-green-700 border-green-200">On Track</Badge>;
    case 'AT_RISK':
      return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">At Risk</Badge>;
    case 'BEHIND':
      return <Badge className="bg-red-100 text-red-700 border-red-200">Behind</Badge>;
  }
};

const KPI_CONFIG = [
  { key: 'calls', label: 'Total Calls', icon: Phone, color: 'text-blue-600', bg: 'bg-blue-50' },
  { key: 'meetings', label: 'Meetings', icon: Users, color: 'text-purple-600', bg: 'bg-purple-50' },
  { key: 'followUps', label: 'Follow-ups', icon: MessageSquare, color: 'text-indigo-600', bg: 'bg-indigo-50' },
  { key: 'leadsToWork', label: 'Leads Worked', icon: Target, color: 'text-orange-600', bg: 'bg-orange-50' },
  { key: 'proposals', label: 'Proposals', icon: FileText, color: 'text-teal-600', bg: 'bg-teal-50' },
  { key: 'revenue', label: 'Revenue', icon: IndianRupee, color: 'text-green-600', bg: 'bg-green-50' },
] as const;

// ==========================================
// KPI PROGRESS CARD
// ==========================================

function KPIProgressCard({
  label,
  icon: Icon,
  color,
  bg,
  metric,
  isRevenue,
  index,
}: {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  bg: string;
  metric: DailyKPIMetric | RevenueKPIMetric;
  isRevenue?: boolean;
  index: number;
}) {
  const completed = isRevenue ? (metric as RevenueKPIMetric).achieved : (metric as DailyKPIMetric).completed;
  const pct = Math.min(metric.percentage, 100);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.08 }}
    >
      <Card className="hover:shadow-md transition-shadow">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className={`p-2 rounded-lg ${bg}`}>
                <Icon className={`h-4 w-4 ${color}`} />
              </div>
              <span className="text-sm font-medium text-gray-700">{label}</span>
            </div>
            <span className="text-xs font-semibold text-gray-500">
              {metric.percentage}%
            </span>
          </div>

          {/* Progress bar */}
          <div className={`w-full h-2.5 rounded-full ${getProgressBg(metric.percentage)} mb-2`}>
            <motion.div
              className={`h-2.5 rounded-full ${getProgressColor(metric.percentage)}`}
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 0.8, delay: index * 0.08 }}
            />
          </div>

          <div className="flex items-center justify-between text-xs text-gray-500">
            <span className="font-semibold text-gray-800">
              {isRevenue ? formatCurrency(completed) : completed}
              <span className="text-gray-400 font-normal">
                {' / '}
                {isRevenue ? formatCurrency(metric.target) : metric.target}
              </span>
            </span>
            <span>
              {metric.remaining > 0
                ? `${isRevenue ? formatCurrency(metric.remaining) : metric.remaining} left`
                : '✓ Done'}
            </span>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

// ==========================================
// OVERALL PROGRESS RING
// ==========================================

function ProgressRing({ progress }: { progress: number }) {
  const radius = 60;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (Math.min(progress, 100) / 100) * circumference;
  const color =
    progress >= 80 ? '#22c55e' : progress >= 50 ? '#eab308' : '#ef4444';

  return (
    <div className="flex flex-col items-center">
      <svg width="160" height="160" className="-rotate-90">
        <circle
          cx="80"
          cy="80"
          r={radius}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth="10"
        />
        <motion.circle
          cx="80"
          cy="80"
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1.2, ease: 'easeOut' }}
        />
      </svg>
      <div className="absolute flex flex-col items-center justify-center" style={{ marginTop: 45 }}>
        <span className="text-3xl font-bold text-gray-900">{progress}%</span>
        <span className="text-xs text-gray-500 mt-1">Daily Progress</span>
      </div>
    </div>
  );
}

// ==========================================
// TEAM MEMBER ROW
// ==========================================

function TeamMemberRow({ member, index }: { member: TeamMemberKPI; index: number }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.05 }}
    >
      <div
        className="border rounded-lg mb-2 hover:shadow-sm transition-shadow cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="p-3 flex items-center justify-between">
          <div className="flex items-center gap-3 flex-1">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <span className="text-white text-xs font-medium">
                {member.user.name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">{member.user.name}</p>
              <p className="text-xs text-gray-500">{member.user.role}</p>
            </div>
          </div>

          {/* Mini KPI indicators */}
          <div className="hidden md:flex items-center gap-4 text-xs text-gray-600">
            <span title="Calls">
              <Phone className="inline h-3 w-3 mr-1" />
              {member.kpis.calls.completed}/{member.kpis.calls.target}
            </span>
            <span title="Meetings">
              <Users className="inline h-3 w-3 mr-1" />
              {member.kpis.meetings.completed}/{member.kpis.meetings.target}
            </span>
            <span title="Follow-ups">
              <MessageSquare className="inline h-3 w-3 mr-1" />
              {member.kpis.followUps.completed}/{member.kpis.followUps.target}
            </span>
          </div>

          {/* Progress + Status */}
          <div className="flex items-center gap-3 ml-4">
            <div className="w-20">
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="font-semibold">{member.overallProgress}%</span>
              </div>
              <div className={`w-full h-1.5 rounded-full ${getProgressBg(member.overallProgress)}`}>
                <div
                  className={`h-1.5 rounded-full ${getProgressColor(member.overallProgress)}`}
                  style={{ width: `${Math.min(member.overallProgress, 100)}%` }}
                />
              </div>
            </div>
            {getStatusBadge(member.status)}
            {expanded ? (
              <ChevronUp className="h-4 w-4 text-gray-400" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-400" />
            )}
          </div>
        </div>

        {/* Expanded KPI details */}
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="px-3 pb-3 border-t"
          >
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 pt-3">
              {KPI_CONFIG.map((cfg) => {
                const m = member.kpis[cfg.key];
                const val = cfg.key === 'revenue' ? (m as RevenueKPIMetric).achieved : (m as DailyKPIMetric).completed;
                return (
                  <div key={cfg.key} className={`p-2 rounded-lg ${cfg.bg}`}>
                    <p className="text-xs text-gray-500">{cfg.label}</p>
                    <p className="text-sm font-semibold">
                      {cfg.key === 'revenue' ? formatCurrency(val) : val}
                      <span className="text-gray-400 font-normal">
                        /{cfg.key === 'revenue' ? formatCurrency(m.target) : m.target}
                      </span>
                    </p>
                    <div className={`w-full h-1 rounded-full mt-1 ${getProgressBg(m.percentage)}`}>
                      <div
                        className={`h-1 rounded-full ${getProgressColor(m.percentage)}`}
                        style={{ width: `${Math.min(m.percentage, 100)}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

// ==========================================
// LEAD SOURCE SECTION
// ==========================================

function LeadSourceSection({ sources }: { sources: LeadSourceKPI[] }) {
  if (!sources || sources.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Lead Sources</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500">No lead sources configured yet.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Lead Source Breakdown</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {sources.map((src, i) => (
            <motion.div
              key={src.id}
              className="flex items-center gap-3"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.05 }}
            >
              <div
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: src.color }}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-800 truncate">
                    {src.name}
                  </span>
                  <span className="text-xs text-gray-500 ml-2">
                    {src.totalLeads} leads
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                  <span>{src.activeLeads} active</span>
                  <span className="text-green-600">{src.wonLeads} won</span>
                  <span className="font-medium">
                    {src.conversionRate}% conv.
                  </span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ==========================================
// LOADING SKELETON
// ==========================================

function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="h-8 bg-gray-200 rounded w-64" />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="h-28 bg-gray-200 rounded-lg" />
        ))}
      </div>
      <div className="h-64 bg-gray-200 rounded-lg" />
    </div>
  );
}

// ==========================================
// LOG ACTIVITY MODAL
// ==========================================

function LogActivityModal({
  isOpen,
  onClose,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { toast } = useToast();
  const [activityType, setActivityType] = useState<'CALL' | 'MEETING' | 'EMAIL'>('CALL');
  const [leadId, setLeadId] = useState('');
  const [notes, setNotes] = useState('');
  const [duration, setDuration] = useState('');
  const [leadSearch, setLeadSearch] = useState('');

  const { data: leadsRaw = [] } = useQuery({
    queryKey: ['leads-for-log', leadSearch],
    queryFn: () => api.getLeads({ query: leadSearch || undefined }),
    staleTime: 30000,
    enabled: isOpen,
  });
  const leads: Lead[] = Array.isArray(leadsRaw) ? leadsRaw : [];

  const logMutation = useMutation({
    mutationFn: (data: { type: string; leadId: string; notes?: string; duration?: number }) =>
      api.logDailyActivity(data),
    onSuccess: () => {
      const selectedLeadName = leadSearch;
      toast({ title: 'Activity Logged', description: `${activityType} logged for ${selectedLeadName}` });
      onSuccess();
      resetForm();
      onClose();
    },
  });

  const resetForm = () => {
    setActivityType('CALL');
    setLeadId('');
    setNotes('');
    setDuration('');
    setLeadSearch('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    logMutation.mutate({
      type: activityType,
      leadId,
      notes: notes.trim() || undefined,
      duration: duration ? parseInt(duration, 10) : undefined,
    });
  };

  if (!isOpen) return null;

  const ACTIVITY_TYPES = [
    { value: 'CALL' as const, label: 'Call', icon: Phone, color: 'bg-blue-100 text-blue-700 border-blue-300' },
    { value: 'MEETING' as const, label: 'Meeting', icon: Users, color: 'bg-purple-100 text-purple-700 border-purple-300' },
    { value: 'EMAIL' as const, label: 'Email', icon: Mail, color: 'bg-green-100 text-green-700 border-green-300' },
  ];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-xl shadow-xl w-full max-w-md"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-5 py-4 border-b">
            <h3 className="text-lg font-semibold text-gray-900">Log Activity</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-5 space-y-4">
            {/* Activity type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
              <div className="flex gap-2">
                {ACTIVITY_TYPES.map(({ value, label, icon: Icon, color }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setActivityType(value)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                      activityType === value ? color + ' ring-2 ring-offset-1 ring-current' : 'bg-gray-50 text-gray-500 border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-1">Call and Meeting count toward your daily KPIs</p>
            </div>

            {/* Lead selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Lead *</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search leads..."
                  value={leadSearch}
                  onChange={(e) => {
                    setLeadSearch(e.target.value);
                    setLeadId('');
                  }}
                  className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              {leads.length > 0 && !leadId && (
                <div className="mt-1 max-h-36 overflow-y-auto border rounded-lg bg-white shadow-sm">
                  {leads.slice(0, 10).map((lead) => (
                    <button
                      key={lead.id}
                      type="button"
                      onClick={() => {
                        setLeadId(lead.id);
                        setLeadSearch(lead.name + (lead.company ? ` - ${lead.company}` : ''));
                      }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b last:border-b-0"
                    >
                      <span className="font-medium text-gray-800">{lead.name}</span>
                      {lead.company && <span className="text-gray-500 ml-1">- {lead.company}</span>}
                    </button>
                  ))}
                </div>
              )}
              {leads.length === 0 && !leadId && leadSearch.length >= 2 && (
                <p className="px-3 py-2 text-sm text-gray-500">No leads found for "{leadSearch}". Try a different search.</p>
              )}
              {leadId && (
                <p className="mt-1 text-xs text-green-600 flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" /> Lead selected
                </p>
              )}
            </div>

            {/* Duration */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Duration (minutes) &mdash; optional</label>
              <input
                type="number"
                placeholder="e.g. 15"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                min="1"
                max="480"
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                placeholder="Brief notes about the activity..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              />
            </div>

            {/* Error */}
            {logMutation.isError && (
              <p className="text-sm text-red-600">
                {logMutation.error instanceof Error ? logMutation.error.message : 'Failed to log activity'}
              </p>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" size="sm" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={!leadId || logMutation.isPending}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {logMutation.isPending ? (
                  <><RefreshCw className="h-3.5 w-3.5 animate-spin mr-1" /> Logging...</>
                ) : (
                  <><Plus className="h-3.5 w-3.5 mr-1" /> Log Activity</>
                )}
              </Button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ==========================================
// MAIN PAGE
// ==========================================

// ==========================================
// WHATSAPP DAILY REPORT — 11-metric team report
// ==========================================

interface DailyReportMetrics {
  totalCalls: number;
  talkTimeMinutes: number;
  leadsMined: number;
  meetingsScheduled: number;
  meetingsDone: number;
  appointmentsTomorrow: number;
  revenue: number;
  proposalSent: number;
  prospectGenerated: number;
  outcomes: Record<string, number>;
}

interface TeamReportExec {
  user: { id: string; name: string; role: string; email: string };
  metrics: DailyReportMetrics;
}

interface TeamReportData {
  date: string;
  team: DailyReportMetrics & { headcount: number };
  executives: TeamReportExec[];
}

const fmtINRCompact = (n: number) => {
  if (!n) return '₹0';
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}k`;
  return `₹${n}`;
};

const initials = (name: string) =>
  name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

const scoreOf = (m: DailyReportMetrics): number => {
  const parts = [
    Math.min(1, m.totalCalls / 40),
    Math.min(1, m.talkTimeMinutes / 120),
    Math.min(1, m.leadsMined / 10),
    Math.min(1, m.meetingsDone / 4),
    Math.min(1, m.revenue / 50000),
    Math.min(1, m.proposalSent / 3),
    Math.min(1, m.prospectGenerated / 3),
  ];
  return Math.round((parts.reduce((a, b) => a + b, 0) / parts.length) * 100);
};

const outcomeColor = (k: string): string =>
  ({
    INTERESTED: 'bg-green-500',
    FOLLOW_UP: 'bg-blue-500',
    BUSY: 'bg-yellow-500',
    SWITCH_OFF: 'bg-gray-400',
    NOT_ANSWERED: 'bg-gray-500',
    INVALID: 'bg-red-500',
  } as Record<string, string>)[k] || 'bg-gray-300';

const outcomeTextColor = (k: string): string =>
  ({
    INTERESTED: 'text-green-700',
    FOLLOW_UP: 'text-blue-700',
    BUSY: 'text-yellow-700',
    SWITCH_OFF: 'text-gray-600',
    NOT_ANSWERED: 'text-gray-500',
    INVALID: 'text-red-700',
  } as Record<string, string>)[k] || 'text-gray-600';

// Executive card — matches WhatsApp Excel format (label | value rows), screenshot-ready
function ExecutiveReportCard({ row, date, onShare }: { row: TeamReportExec; date: string; onShare: (r: TeamReportExec) => void }) {
  const { user, metrics } = row;
  const score = scoreOf(metrics);
  const circumference = 2 * Math.PI * 22;
  const dash = (score / 100) * circumference;
  const ringColor = score >= 70 ? '#16a34a' : score >= 40 ? '#eab308' : '#ef4444';
  const scoreText = score >= 70 ? 'text-green-600' : score >= 40 ? 'text-yellow-600' : 'text-red-600';

  const outcomes = metrics.outcomes || {};
  const outcomeTotal = Object.values(outcomes).reduce((a, b) => a + (b as number), 0);
  const connectRate = metrics.totalCalls > 0
    ? Math.round(((outcomes.INTERESTED || 0) + (outcomes.FOLLOW_UP || 0)) / metrics.totalCalls * 100)
    : 0;
  const avgCallMin = metrics.totalCalls > 0
    ? (metrics.talkTimeMinutes / metrics.totalCalls).toFixed(1)
    : '0';

  // Format date as DD-MM-YY like the WhatsApp format
  const [y, m, d] = date.split('-');
  const displayDate = `${d}-${m}-${y.slice(2)}`;

  return (
    <Card className="group relative hover:shadow-lg transition-shadow overflow-hidden border-gray-200">
      <button
        onClick={() => onShare(row)}
        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 bg-white border border-gray-200 rounded hover:border-blue-300 shadow-sm z-10"
        title="Share / copy snapshot line"
      >
        <Camera className="h-3 w-3 text-gray-500" />
      </button>

      {/* HEADER — branded strip like WhatsApp format */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-3 text-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/20 border-2 border-white/30 flex items-center justify-center font-bold text-sm shrink-0 backdrop-blur">
            {initials(user.name)}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-widest text-blue-100 font-medium">WTF Amplify — Daily Report</div>
            <div className="font-bold text-sm truncate leading-tight mt-0.5">{user.name}</div>
            <div className="text-[10px] text-blue-100">{user.role}</div>
          </div>
          <div className="relative w-12 h-12 shrink-0">
            <svg className="absolute inset-0 -rotate-90" viewBox="0 0 48 48">
              <circle cx="24" cy="24" r="22" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="3" />
              <circle cx="24" cy="24" r="22" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round"
                strokeDasharray={`${dash} ${circumference}`}
                style={{ transition: 'stroke-dasharray 0.6s' }} />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center font-bold text-sm text-white">
              {score}
            </div>
          </div>
        </div>
      </div>

      {/* BODY — Excel-style label | value rows (EXACT WhatsApp format) */}
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <tbody>
            <ReportRow label="Date" value={displayDate} mono />
            <ReportRow label="Total Calls" value={metrics.totalCalls} bold />
            <ReportRow label="Talk Time (mins)" value={metrics.talkTimeMinutes} />
            <ReportRow label="Leads Mined" value={metrics.leadsMined} />
            <ReportRow label="Meetings Scheduled" value={metrics.meetingsScheduled} />
            <ReportRow label="Meetings Done" value={metrics.meetingsDone} highlight={metrics.meetingsDone > 0} />
            <ReportRow label="Appt. fixed for tomorrow" value={metrics.appointmentsTomorrow} />
            <ReportRow
              label="Revenue (₹)"
              value={metrics.revenue > 0 ? metrics.revenue.toLocaleString('en-IN') : '—'}
              highlight={metrics.revenue > 0}
            />
            <ReportRow label="Proposal Sent" value={metrics.proposalSent} highlight={metrics.proposalSent > 0} />
            <ReportRow label="Prospect Generated" value={metrics.prospectGenerated} highlight={metrics.prospectGenerated > 0} />
          </tbody>
        </table>

        {/* ADDITIONAL KPI INSIGHTS — beyond WhatsApp */}
        <div className="bg-gray-50 border-t border-gray-200 px-4 py-3 grid grid-cols-3 gap-3">
          <div>
            <div className="text-[9px] text-gray-500 uppercase tracking-wider font-medium">Connect Rate</div>
            <div className={`text-sm font-bold mt-0.5 ${connectRate >= 30 ? 'text-green-600' : connectRate > 0 ? 'text-yellow-600' : 'text-gray-400'}`}>
              {connectRate}%
            </div>
          </div>
          <div>
            <div className="text-[9px] text-gray-500 uppercase tracking-wider font-medium">Avg Call</div>
            <div className="text-sm font-bold mt-0.5 text-gray-900">{avgCallMin}m</div>
          </div>
          <div>
            <div className="text-[9px] text-gray-500 uppercase tracking-wider font-medium">Score</div>
            <div className={`text-sm font-bold mt-0.5 ${scoreText}`}>{score}/100</div>
          </div>
        </div>

        {/* CALL OUTCOMES */}
        {outcomeTotal > 0 && (
          <div className="px-4 py-3 border-t border-gray-100">
            <div className="text-[9px] text-gray-500 uppercase tracking-widest font-semibold mb-2">Call Outcomes</div>
            <div className="flex h-2 rounded-full overflow-hidden bg-gray-100 mb-2">
              {Object.entries(outcomes).map(([k, v]) =>
                (v as number) > 0 ? (
                  <span key={k} className={`h-full ${outcomeColor(k)}`} style={{ width: `${((v as number) / outcomeTotal) * 100}%` }} />
                ) : null
              )}
            </div>
            <div className="grid grid-cols-3 gap-x-3 gap-y-1 text-[10px]">
              {outcomes.INTERESTED > 0 && <span className={outcomeTextColor('INTERESTED')}>● INT · {outcomes.INTERESTED}</span>}
              {outcomes.FOLLOW_UP > 0 && <span className={outcomeTextColor('FOLLOW_UP')}>● FUP · {outcomes.FOLLOW_UP}</span>}
              {outcomes.BUSY > 0 && <span className={outcomeTextColor('BUSY')}>● BUSY · {outcomes.BUSY}</span>}
              {outcomes.SWITCH_OFF > 0 && <span className={outcomeTextColor('SWITCH_OFF')}>● S/O · {outcomes.SWITCH_OFF}</span>}
              {outcomes.NOT_ANSWERED > 0 && <span className={outcomeTextColor('NOT_ANSWERED')}>● N/A · {outcomes.NOT_ANSWERED}</span>}
              {outcomes.INVALID > 0 && <span className={outcomeTextColor('INVALID')}>● INV · {outcomes.INVALID}</span>}
            </div>
          </div>
        )}

        {/* SHARE BUTTON */}
        <div className="px-4 py-2.5 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
          <span className="text-[10px] text-gray-500 tracking-wider uppercase font-medium">{displayDate}</span>
          <button
            onClick={() => onShare(row)}
            className="text-[10px] font-semibold text-blue-600 hover:text-blue-700 uppercase tracking-wider flex items-center gap-1"
          >
            <Share2 className="h-3 w-3" /> Share
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

function ReportRow({ label, value, bold, highlight, mono }: { label: string; value: React.ReactNode; bold?: boolean; highlight?: boolean; mono?: boolean }) {
  return (
    <tr className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50">
      <td className="px-4 py-2 text-xs text-gray-600 font-medium w-1/2">{label}</td>
      <td className={`px-4 py-2 text-right ${mono ? 'font-mono text-xs' : 'text-sm'} ${
        bold ? 'font-bold text-gray-900' :
        highlight ? 'font-semibold text-green-700' :
        'text-gray-900'
      }`}>
        {value || '—'}
      </td>
    </tr>
  );
}

// Log Call modal — call-specific (separate from LogActivityModal for generic activities)
function LogCallModal({ isOpen, onClose, onSuccess }: { isOpen: boolean; onClose: () => void; onSuccess: () => void }) {
  const { toast } = useToast();
  const [leadSearch, setLeadSearch] = useState('');
  const [selectedLead, setSelectedLead] = useState<{ id: string; name: string; company?: string } | null>(null);
  const [outcome, setOutcome] = useState('');
  const [duration, setDuration] = useState('');
  const [notes, setNotes] = useState('');

  const { data: leadsResp } = useQuery({
    queryKey: ['leads-search-call', leadSearch],
    queryFn: () => api.getLeads({ query: leadSearch || undefined }),
    enabled: isOpen && leadSearch.length >= 2,
  });
  const leads: Array<{ id: string; name: string; company?: string }> = (leadsResp as any)?.data || [];

  const saveMutation = useMutation({
    mutationFn: () =>
      api.logCall({
        leadId: selectedLead!.id,
        outcome,
        durationMinutes: duration ? parseInt(duration, 10) : undefined,
        notes: notes || undefined,
      }),
    onSuccess: () => {
      toast({ title: 'Call logged', description: `${outcome} for ${selectedLead?.name}` });
      setLeadSearch(''); setSelectedLead(null); setOutcome(''); setDuration(''); setNotes('');
      onSuccess();
      onClose();
    },
    onError: (e: any) => toast({ title: 'Failed to log call', description: e.message, variant: 'destructive' }),
  });

  const OUTCOME_OPTS = [
    { id: 'INTERESTED', label: 'Interested', cls: 'border-green-300 text-green-700 bg-green-50' },
    { id: 'FOLLOW_UP', label: 'Follow-up', cls: 'border-blue-300 text-blue-700 bg-blue-50' },
    { id: 'BUSY', label: 'Busy / Call back', cls: 'border-yellow-300 text-yellow-700 bg-yellow-50' },
    { id: 'SWITCH_OFF', label: 'Switch off', cls: 'border-gray-300 text-gray-700 bg-gray-50' },
    { id: 'NOT_ANSWERED', label: 'Not answered', cls: 'border-gray-300 text-gray-600 bg-gray-50' },
    { id: 'INVALID', label: 'Invalid number', cls: 'border-red-300 text-red-700 bg-red-50' },
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Log a Call</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-4 space-y-4">
          {/* Lead search */}
          <div>
            <label className="text-xs font-medium text-gray-700 block mb-1.5">Lead</label>
            {selectedLead ? (
              <div className="flex items-center justify-between p-2.5 bg-blue-50 border border-blue-200 rounded-md">
                <div>
                  <div className="font-medium text-sm text-gray-900">{selectedLead.name}</div>
                  {selectedLead.company && <div className="text-xs text-gray-500">{selectedLead.company}</div>}
                </div>
                <button onClick={() => setSelectedLead(null)} className="text-gray-400 hover:text-red-600">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text" value={leadSearch} onChange={e => setLeadSearch(e.target.value)}
                  placeholder="Type at least 2 characters..."
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                {leads.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md max-h-60 overflow-auto z-10 shadow-lg">
                    {leads.slice(0, 10).map(l => (
                      <button key={l.id} onClick={() => { setSelectedLead(l); setLeadSearch(''); }}
                        className="w-full text-left p-2.5 hover:bg-blue-50 border-b border-gray-100 last:border-b-0">
                        <div className="text-sm font-medium text-gray-900">{l.name}</div>
                        {l.company && <div className="text-xs text-gray-500">{l.company}</div>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Outcome */}
          <div>
            <label className="text-xs font-medium text-gray-700 block mb-1.5">Outcome</label>
            <div className="grid grid-cols-2 gap-2">
              {OUTCOME_OPTS.map(o => (
                <button key={o.id} onClick={() => setOutcome(o.id)}
                  className={`px-3 py-2 text-sm font-medium rounded-md border transition-colors ${
                    outcome === o.id ? o.cls : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}>
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          {/* Duration + Notes */}
          <div className="grid grid-cols-[120px_1fr] gap-3">
            <div>
              <label className="text-xs font-medium text-gray-700 block mb-1.5">Duration (min)</label>
              <input type="number" min="0" value={duration} onChange={e => setDuration(e.target.value)} placeholder="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 block mb-1.5">Notes (optional)</label>
              <input type="text" value={notes} onChange={e => setNotes(e.target.value)} placeholder="What happened?"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>
        </div>
        <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={() => saveMutation.mutate()} disabled={!selectedLead || !outcome || saveMutation.isPending}
            className="bg-blue-600 hover:bg-blue-700 text-white">
            {saveMutation.isPending ? 'Logging...' : 'Log Call'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function DailyKPIPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const isManager = ['CEO', 'GROUP_MANAGER', 'MANAGER'].includes(user?.role || '');
  const isTopLevel = ['CEO', 'GROUP_MANAGER'].includes(user?.role || '');
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);
  const [isLogCallOpen, setIsLogCallOpen] = useState(false);
  const queryClient = useQueryClient();

  // Report date selector (defaults to today)
  const todayISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const [reportDate, setReportDate] = useState(todayISO());
  const isToday = reportDate === todayISO();

  // Individual KPI query
  const {
    data: kpiResponse,
    isLoading: kpiLoading,
    error: kpiError,
    refetch: refetchKPI,
    dataUpdatedAt,
  } = useQuery({
    queryKey: ['daily-kpi'],
    queryFn: () => api.getDailyKPI(),
    staleTime: 15000,
    refetchInterval: 30000,
  });

  // Team KPI query (managers only)
  const {
    data: teamResponse,
    isLoading: teamLoading,
    error: teamError,
    refetch: refetchTeam,
  } = useQuery({
    queryKey: ['daily-kpi-team'],
    queryFn: () => api.getDailyKPITeam(),
    staleTime: 15000,
    refetchInterval: 30000,
    enabled: isManager,
  });

  // Lead sources query
  const { data: sourcesResponse } = useQuery({
    queryKey: ['daily-kpi-sources'],
    queryFn: () => api.getDailyKPISources(),
    staleTime: 60000,
  });

  // Team Daily Report (WhatsApp format) — 11 metrics per executive, live for today
  const { data: teamReportResp, refetch: refetchTeamReport } = useQuery({
    queryKey: ['daily-kpi-team-report', reportDate],
    queryFn: () => api.getDailyKPITeamReport({ date: reportDate }),
    refetchInterval: isToday ? 15000 : false,
    enabled: isManager,
  });
  const teamReport: TeamReportData | undefined = (teamReportResp as any)?.data;

  const handleCopyWhatsapp = async () => {
    try {
      const text = await api.exportDailyReport({ date: reportDate, format: 'whatsapp' });
      await navigator.clipboard.writeText(text);
      toast({ title: 'Copied to clipboard', description: 'Paste in your WhatsApp group.' });
    } catch (e: any) {
      toast({ title: 'Copy failed', description: e.message, variant: 'destructive' });
    }
  };

  const handleDownloadCsv = async () => {
    try {
      const text = await api.exportDailyReport({ date: reportDate, format: 'csv' });
      const blob = new Blob([text], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `daily-report-${reportDate}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      toast({ title: 'Export failed', description: e.message, variant: 'destructive' });
    }
  };

  const handleShareCard = (row: TeamReportExec) => {
    const [y, m, d] = reportDate.split('-');
    const displayDate = `${d}-${m}-${y.slice(2)}`;
    const line =
      `*${row.user.name} — WTF Amplify Daily Report*\n` +
      `Date: ${displayDate}\n` +
      `Total Calls: ${row.metrics.totalCalls}\n` +
      `Talk Time: ${row.metrics.talkTimeMinutes} mins\n` +
      `Leads Mined: ${row.metrics.leadsMined}\n` +
      `Meetings Done: ${row.metrics.meetingsDone}\n` +
      `Revenue: ₹${Number(row.metrics.revenue || 0).toLocaleString('en-IN')}\n` +
      `Proposals: ${row.metrics.proposalSent}\n` +
      `Prospects: ${row.metrics.prospectGenerated}`;
    if (navigator.share) {
      navigator.share({ title: `${row.user.name} — Daily Report`, text: line }).catch(() => {});
    } else {
      navigator.clipboard.writeText(line);
      toast({ title: 'Copied snapshot', description: 'Paste into WhatsApp.' });
    }
  };

  const kpiData: DailyKPIData | undefined = kpiResponse?.data;
  const teamData: TeamKPIData | undefined = teamResponse?.data;
  const sourceData: LeadSourceKPI[] | undefined = sourcesResponse?.data?.sources;

  const handleRefresh = () => {
    refetchKPI();
    if (isManager) refetchTeam();
  };

  const lastUpdated = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Daily KPI Dashboard</h1>
            <p className="text-sm text-gray-500 mt-1">
              {kpiData ? formatDate(kpiData.date) : 'Loading...'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Live indicator */}
            <div className="flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" />
              </span>
              <span className="text-xs text-green-600 font-medium">LIVE</span>
            </div>
            {lastUpdated && (
              <span className="text-xs text-gray-400">Updated {lastUpdated}</span>
            )}
            <Button
              size="sm"
              onClick={() => setIsLogCallOpen(true)}
              className="gap-1 bg-green-600 hover:bg-green-700 text-white"
            >
              <Phone className="h-3.5 w-3.5" />
              Log Call
            </Button>
            <Button
              size="sm"
              onClick={() => setIsLogModalOpen(true)}
              className="gap-1 bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Plus className="h-3.5 w-3.5" />
              Log Activity
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              className="gap-1"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Error States */}
        {kpiError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3" role="alert">
            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800">Failed to load KPI data</p>
              <p className="text-xs text-red-600 mt-0.5">
                {kpiError instanceof Error ? kpiError.message : 'Please check your connection and try again.'}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => refetchKPI()} className="text-red-700 border-red-300 hover:bg-red-100">
              Retry
            </Button>
          </div>
        )}

        {isManager && teamError && !teamLoading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3" role="alert">
            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800">Failed to load team KPI data</p>
              <p className="text-xs text-red-600 mt-0.5">
                {teamError instanceof Error ? teamError.message : 'Please check your connection and try again.'}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => refetchTeam()} className="text-red-700 border-red-300 hover:bg-red-100">
              Retry
            </Button>
          </div>
        )}

        {/* ════════════════════════════════════════════════════
            DAILY REPORT — WhatsApp format replacement
            Shows every exec as a self-contained card (screenshot-ready)
            Updates every 15s when viewing today
           ════════════════════════════════════════════════════ */}
        {isManager && (
          <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Daily Report</h2>
                <p className="text-sm text-gray-500 mt-0.5">
                  Replaces the WhatsApp format · {teamReport?.executives.length || 0} executives
                  {isToday && ' · live (15s)'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <input
                    type="date"
                    value={reportDate}
                    max={todayISO()}
                    onChange={e => setReportDate(e.target.value)}
                    className="px-2.5 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {!isToday && (
                    <Button size="sm" variant="outline" onClick={() => setReportDate(todayISO())}>Today</Button>
                  )}
                </div>
                <Button size="sm" variant="outline" onClick={handleCopyWhatsapp} className="gap-1">
                  <Copy className="h-3.5 w-3.5" /> Copy WA
                </Button>
                <Button size="sm" variant="outline" onClick={handleDownloadCsv} className="gap-1">
                  <Download className="h-3.5 w-3.5" /> CSV
                </Button>
              </div>
            </div>

            {/* TEAM TOTALS — the 11 WhatsApp fields aggregated */}
            {teamReport && (
              <Card className="mb-4 overflow-hidden">
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 px-4 py-2">
                  <div className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Team Totals</div>
                </div>
                <CardContent className="p-0">
                  <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-10 divide-x divide-y md:divide-y-0 divide-gray-100">
                    <TotalCell label="Headcount" value={teamReport.team.headcount} />
                    <TotalCell label="Calls" value={teamReport.team.totalCalls} highlight={teamReport.team.totalCalls > 0} />
                    <TotalCell label="Talk (m)" value={teamReport.team.talkTimeMinutes} />
                    <TotalCell label="Leads Mined" value={teamReport.team.leadsMined} />
                    <TotalCell label="Mtgs Sch" value={teamReport.team.meetingsScheduled} />
                    <TotalCell label="Mtgs Done" value={teamReport.team.meetingsDone} highlight={teamReport.team.meetingsDone > 0} />
                    <TotalCell label="Tmrw Appt" value={teamReport.team.appointmentsTomorrow} />
                    <TotalCell label="Proposals" value={teamReport.team.proposalSent} />
                    <TotalCell label="Prospects" value={teamReport.team.prospectGenerated} />
                    <TotalCell label="Revenue" value={fmtINRCompact(teamReport.team.revenue)} highlight={teamReport.team.revenue > 0} />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* EXECUTIVE CARDS GRID */}
            {teamReport?.executives && teamReport.executives.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {teamReport.executives.map(row => (
                  <ExecutiveReportCard key={row.user.id} row={row} date={reportDate} onShare={handleShareCard} />
                ))}
              </div>
            ) : (
              <Card className="p-8 text-center">
                <Users className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">Loading team report...</p>
              </Card>
            )}

            {/* CALL OUTCOME DISTRIBUTION — team-wide */}
            {teamReport && teamReport.team.totalCalls > 0 && (
              <Card className="mt-4">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span>Team Call Outcomes Today</span>
                    <span className="text-sm text-gray-500 font-normal">{teamReport.team.totalCalls} calls</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex h-8 rounded-md overflow-hidden border border-gray-200">
                    {Object.entries(teamReport.team.outcomes).map(([k, v]) =>
                      (v as number) > 0 ? (
                        <div key={k} className={`${outcomeColor(k)} text-white text-xs font-semibold flex items-center justify-center px-2`}
                          style={{ width: `${((v as number) / teamReport.team.totalCalls) * 100}%` }}
                          title={`${k}: ${v}`}>
                          {(v as number)} {k.replace('_', ' ')}
                        </div>
                      ) : null
                    )}
                  </div>
                  <div className="flex flex-wrap gap-4 mt-3 text-xs text-gray-600">
                    <Legend color="bg-green-500" label="Interested" />
                    <Legend color="bg-blue-500" label="Follow-up" />
                    <Legend color="bg-yellow-500" label="Busy" />
                    <Legend color="bg-gray-400" label="Switch off" />
                    <Legend color="bg-gray-500" label="Not answered" />
                    <Legend color="bg-red-500" label="Invalid" />
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {kpiLoading ? (
          <LoadingSkeleton />
        ) : kpiData ? (
          <>
            {/* Top-level role banner */}
            {isTopLevel && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-center gap-2">
                <Users className="h-5 w-5 text-blue-600 flex-shrink-0" />
                <div>
                  <span className="text-base text-blue-800 font-semibold">
                    Viewing team-wide KPI performance
                  </span>
                  <p className="text-xs text-blue-600 mt-0.5">Your personal targets are not shown here.</p>
                </div>
              </div>
            )}

            {/* My KPIs Section — hidden for CEO/GROUP_MANAGER */}
            {!isTopLevel && (
              <div>
                <h2 className="text-lg font-semibold text-gray-800 mb-3">
                  {isManager ? 'My Performance' : 'Today\'s KPIs'}
                </h2>

                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                  {/* Progress Ring */}
                  <Card className="flex items-center justify-center p-6 relative">
                    <ProgressRing progress={kpiData.overallProgress} />
                  </Card>

                  {/* KPI Cards Grid */}
                  <div className="lg:col-span-3 grid grid-cols-2 md:grid-cols-3 gap-3">
                    {KPI_CONFIG.map((cfg, i) => (
                      <KPIProgressCard
                        key={cfg.key}
                        label={cfg.label}
                        icon={cfg.icon}
                        color={cfg.color}
                        bg={cfg.bg}
                        metric={kpiData.kpis[cfg.key]}
                        isRevenue={cfg.key === 'revenue'}
                        index={i}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Activity Summary — hidden for CEO/GROUP_MANAGER */}
            {!isTopLevel && kpiData.activities && kpiData.activities.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Today's Activity Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-3">
                    {kpiData.activities.map((act) => (
                      <div
                        key={act.type}
                        className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg"
                      >
                        <span className="text-xs font-medium text-gray-500">
                          {act.type}
                        </span>
                        <Badge variant="secondary" className="text-xs">
                          {act.count}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Team View (managers only) */}
            {isManager && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-semibold text-gray-800">
                    Team Performance
                  </h2>
                  {teamData && (
                    <div className="flex items-center gap-4 text-sm">
                      <span className="flex items-center gap-1 text-green-600">
                        <CheckCircle className="h-4 w-4" />
                        {teamData.members.filter((m) => m.status === 'ON_TRACK').length} On Track
                      </span>
                      <span className="flex items-center gap-1 text-yellow-600">
                        <Clock className="h-4 w-4" />
                        {teamData.members.filter((m) => m.status === 'AT_RISK').length} At Risk
                      </span>
                      <span className="flex items-center gap-1 text-red-600">
                        <AlertTriangle className="h-4 w-4" />
                        {teamData.members.filter((m) => m.status === 'BEHIND').length} Behind
                      </span>
                    </div>
                  )}
                </div>

                {teamLoading ? (
                  <div className="space-y-2 animate-pulse">
                    {[...Array(4)].map((_, i) => (
                      <div key={i} className="h-16 bg-gray-200 rounded-lg" />
                    ))}
                  </div>
                ) : teamData && teamData.members.length > 0 ? (
                  <>
                    {/* Team Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                      <Card className="p-3">
                        <p className="text-xs text-gray-500">Team Progress</p>
                        <p className="text-xl font-bold text-gray-900">{teamData.teamProgress}%</p>
                      </Card>
                      <Card className="p-3">
                        <p className="text-xs text-gray-500">Total Calls</p>
                        <p className="text-xl font-bold text-blue-600">{teamData.summary.totalCalls}</p>
                      </Card>
                      <Card className="p-3">
                        <p className="text-xs text-gray-500">Total Meetings</p>
                        <p className="text-xl font-bold text-purple-600">{teamData.summary.totalMeetings}</p>
                      </Card>
                      <Card className="p-3">
                        <p className="text-xs text-gray-500">Revenue Won</p>
                        <p className="text-xl font-bold text-green-600">
                          {formatCurrency(teamData.summary.totalRevenue)}
                        </p>
                      </Card>
                    </div>

                    {/* Team Members */}
                    <div>
                      {teamData.members.map((member, i) => (
                        <TeamMemberRow key={member.user.id} member={member} index={i} />
                      ))}
                    </div>

                    {/* Needs Attention */}
                    {teamData.summary.needsAttention.length > 0 && (
                      <Card className="mt-4 border-red-200 bg-red-50">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <AlertTriangle className="h-4 w-4 text-red-600" />
                            <span className="text-sm font-semibold text-red-700">
                              Needs Attention
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {teamData.summary.needsAttention.map((person) => (
                              <span
                                key={person.id}
                                className="text-xs bg-white px-2 py-1 rounded border border-red-200 text-red-700"
                              >
                                {person.name} ({person.progress}%)
                              </span>
                            ))}
                          </div>
                          <p className="text-xs text-gray-500 mt-2">Reach out to these team members to identify and remove blockers</p>
                        </CardContent>
                      </Card>
                    )}
                  </>
                ) : (
                  <Card className="p-8 text-center">
                    <Users className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No team members found.</p>
                  </Card>
                )}
              </div>
            )}

            {/* Lead Sources */}
            <LeadSourceSection sources={sourceData || []} />

            {/* EOD Comparison hint */}
            <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
              <CardContent className="p-4 flex items-center gap-3">
                <TrendingUp className="h-5 w-5 text-blue-600 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-blue-800">
                    End-of-Day Performance
                  </p>
                  <p className="text-xs text-blue-600 mt-0.5">
                    This dashboard auto-refreshes every 30 seconds. Share a screenshot at EOD to show
                    daily KPI targets vs actual performance across the team.
                  </p>
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          <Card className="p-8 text-center">
            <Target className="h-10 w-10 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-500">Unable to load KPI data.</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={handleRefresh}>
              Try Again
            </Button>
          </Card>
        )}
      </div>

      {/* Log Activity Modal */}
      <LogActivityModal
        isOpen={isLogModalOpen}
        onClose={() => setIsLogModalOpen(false)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['daily-kpi'] });
          queryClient.invalidateQueries({ queryKey: ['daily-kpi-team'] });
        }}
      />

      {/* Log Call Modal */}
      <LogCallModal
        isOpen={isLogCallOpen}
        onClose={() => setIsLogCallOpen(false)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['daily-kpi'] });
          queryClient.invalidateQueries({ queryKey: ['daily-kpi-team'] });
          queryClient.invalidateQueries({ queryKey: ['daily-kpi-team-report'] });
        }}
      />
    </DashboardLayout>
  );
}

// Helper components
function TotalCell({ label, value, highlight }: { label: string; value: React.ReactNode; highlight?: boolean }) {
  return (
    <div className="px-3 py-3 text-center bg-white">
      <div className="text-[9px] text-gray-500 uppercase tracking-wider font-medium">{label}</div>
      <div className={`text-lg font-bold leading-tight mt-1 ${highlight ? 'text-green-600' : 'text-gray-900'}`}>
        {value}
      </div>
    </div>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className={`w-2.5 h-2.5 rounded ${color}`} />
      {label}
    </span>
  );
}
